import { SKY_WORLD, SKY_LEVIATHAN_FLIGHT } from './86-sky-world-scale.mjs';

const clamp = (value, low, high) => Math.max(low, Math.min(high, value));
const smooth = (value) => {
  const t = clamp(value, 0, 1);
  return t * t * (3 - 2 * t);
};

/** Industrial flight corridor in world metres. Wave clearance advances the
 * destination, never teleports an actor or substitutes a backdrop for altitude.
 * The launch lane is above the sea; the final lane meets the real whale hull. */
export const SKY_LEVIATHAN_JOURNEY = Object.freeze({
  seaY: 0,
  startOriginY: 6 - SKY_WORLD.pilotAimHeight,
  cruiseOriginY: SKY_LEVIATHAN_FLIGHT.cruiseY - 4,
  climbSpeed: 3,
  climbAcceleration: 1.4,
  cityDistance: 100,
  shorelineStartZ: -110,
  shorelineEndZ: 80,
  cloudCenterY: 62,
  cloudHeight: 18,
});

/** Establish an upcoming stage's physical launch lane at its stage boundary.
 * Existing high/low steering is clamped into that lane; other stages clear the
 * descriptor and keep their original terrain-relative movement contract. Boss-only
 * starts use the existing cruise lane without inventing cleared approach waves. */
export function prepareSkyJourneyAltitude(state, stage) {
  if (stage?.encounterId !== 'gilded-leviathan') {
    state.journeyAltitude = null;
    return null;
  }
  if (state.journeyAltitude && state.journeyAltitude.sector === state.sector)
    return state.journeyAltitude;
  const bossStart = state.startMode === 'boss';
  const originY = bossStart
    ? SKY_LEVIATHAN_JOURNEY.cruiseOriginY
    : SKY_LEVIATHAN_JOURNEY.startOriginY;
  state.journeyAltitude = { sector: state.sector, originY, velocity: 0, settled: bossStart };
  state.y = clamp(
    state.y,
    originY + SKY_WORLD.pilotAimHeight,
    originY + SKY_WORLD.maximumAimHeight,
  );
  state.locomotion = 'fly';
  return state.journeyAltitude;
}

/** Advance the whole formation's lane with bounded speed/acceleration. Existing
 * projectiles retain their world trajectories; pilots and returning enemies
 * climb continuously. Returns the current world altitude of the local lane. */
export function stepSkyJourneyAltitude(state, stage, dt) {
  const lane = prepareSkyJourneyAltitude(state, stage);
  if (!lane || state.leviathan || !(dt > 0) || !Number.isFinite(dt)) return lane?.originY ?? 0;
  const spec = SKY_LEVIATHAN_JOURNEY;
  const progress = state.startMode === 'boss' ? 1 : clamp((state.journey?.cleared ?? 0) / 5, 0, 1);
  const target = spec.startOriginY + (spec.cruiseOriginY - spec.startOriginY) * smooth(progress);
  const remaining = Math.max(0, target - lane.originY);
  // Trapezoidal integration reserves the exact remaining braking distance;
  // clamping an overshooting step would otherwise snap velocity at each wave.
  const previousVelocity = lane.velocity;
  const accelerationStep = spec.climbAcceleration * dt;
  const discriminant =
    accelerationStep ** 2 -
    4 * accelerationStep * previousVelocity +
    8 * spec.climbAcceleration * remaining;
  const safeVelocity = Math.max(0, (Math.sqrt(Math.max(0, discriminant)) - accelerationStep) / 2);
  const desired = Math.min(spec.climbSpeed, safeVelocity);
  lane.velocity += clamp(desired - lane.velocity, -accelerationStep, accelerationStep);
  const delta = Math.min(remaining, ((previousVelocity + lane.velocity) * dt) / 2);
  lane.originY += delta;
  if (target - lane.originY < 1e-8 && lane.velocity < accelerationStep) {
    lane.originY = target;
    lane.velocity = 0;
  }
  lane.settled = lane.originY >= spec.cruiseOriginY - 0.001 && lane.velocity === 0;
  state.y += delta;
  for (const enemy of state.enemies) {
    if (!enemy.journeyMember) continue;
    enemy.y += delta;
    enemy.centerY += delta;
    enemy.laneOriginY = lane.originY;
    enemy.journeyClimbVelocity = lane.velocity;
    for (const point of enemy.journeyReturn?.points ?? []) point.y += delta;
  }
  return lane.originY;
}

/** Presentation samples physical progress without owning a second timer.
 * A frozen cinematic snapshot yields the same coastline/cloud composition.
 * The dry crash arena is fully inland before the guardian becomes actionable. */
export function skyLeviathanJourneyView(state) {
  const spec = SKY_LEVIATHAN_JOURNEY;
  const lane =
    state.journeyAltitude && state.journeyAltitude.sector === state.sector
      ? state.journeyAltitude
      : null;
  const progress = state.leviathan
    ? 1
    : clamp(
        ((lane?.originY ?? spec.startOriginY) - spec.startOriginY) /
          (spec.cruiseOriginY - spec.startOriginY),
        0,
        1,
      );
  const descent = clamp(state.leviathan?.systems?.crashDescent ?? 0, 0, 1);
  return {
    progress,
    landVisibility: 1 - smooth((progress - 0.55) / 0.45) * (1 - descent),
    deckOpacity: smooth((progress - 0.9) / 0.1) * (1 - descent * 0.9),
    cityOffsetZ: -spec.cityDistance * (1 - smooth(progress)),
    shorelineZ:
      spec.shorelineStartZ + (spec.shorelineEndZ - spec.shorelineStartZ) * smooth(progress),
    cloudOpacity: 0.35 + progress * 0.37,
  };
}
